import {useCallback, useEffect, useMemo, useState} from 'react'
import {Pressable, View} from 'react-native'
import {LinearGradient} from 'expo-linear-gradient'
import {type AppBskyUnspeccedDefs, moderateProfile} from '@atproto/api'
import {msg, plural, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {HITSLOP_10} from '#/lib/constants'
import {makeSearchLink} from '#/lib/routes/links'
import {getSafeUiLabel} from '#/lib/strings/ui-labels'
import {logger} from '#/logger'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {useTrendingSettings} from '#/state/preferences/trending'
import {useGetTrendsQuery} from '#/state/queries/trending/useGetTrendsQuery'
import {useTrendingConfig} from '#/state/service-config'
import {LoadingPlaceholder} from '#/view/com/util/LoadingPlaceholder'
import {formatCount} from '#/view/com/util/numeric/format'
import {
  atoms as a,
  select,
  useBreakpoints,
  useTheme,
  type ViewStyleProp,
  web,
} from '#/alf'
import {AvatarStack} from '#/components/AvatarStack'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {type Props as SVGIconProps} from '#/components/icons/common'
import {Flame_Stroke2_Corner1_Rounded as FlameIcon} from '#/components/icons/Flame'
import {Menu_Stroke2_Corner0_Rounded as MenuIcon} from '#/components/icons/Menu'
import {TimesLarge_Stroke2_Corner0_Rounded as XIcon} from '#/components/icons/Times'
import {Trending3_Stroke2_Corner1_Rounded as TrendingIcon} from '#/components/icons/Trending'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {Link} from '#/components/Link'
import * as Menu from '#/components/Menu'
import {SubtleHover} from '#/components/SubtleHover'
import {Text} from '#/components/Typography'

const TOPIC_COUNT = 5
const EXPLORE_DEBUG_FLAG = 'tellus_debug_explore'
type ExploreTabKey =
  | 'for-you'
  | 'trending'
  | 'news'
  | 'sports'
  | 'entertainment'

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV !== 'production'

function exploreDebug(event: string, payload?: unknown) {
  if (!isDev || typeof window === 'undefined' || !window.localStorage) return
  try {
    if (window.localStorage.getItem(EXPLORE_DEBUG_FLAG) !== '1') return
  } catch {
    return
  }
  if (payload !== undefined) {
    console.debug(`[ExploreTrending] ${event}`, payload)
  } else {
    console.debug(`[ExploreTrending] ${event}`)
  }
}

const INTERNAL_TREND_HOSTS = [
  'bsky.app',
  'tellus.app',
  'tellus.social',
] as const

function normalizeTrendSearchQuery(
  topic?: string | null,
  displayName?: string | null,
) {
  const query = (topic || displayName || '').trim().replace(/^#/, '').trim()
  return query || null
}

function isInternalTrendHost(hostname: string) {
  const host = hostname.toLowerCase()
  return INTERNAL_TREND_HOSTS.some(
    trusted => host === trusted || host.endsWith(`.${trusted}`),
  )
}

function toInternalTrendLink(rawLink: string): string | null {
  if (!rawLink) return null
  if (rawLink.startsWith('/')) return rawLink

  try {
    const parsed = new URL(rawLink)
    if (!isInternalTrendHost(parsed.hostname)) return null

    if (parsed.pathname === '/search') {
      const q = parsed.searchParams.get('q')?.trim()
      return q ? makeSearchLink({query: q}) : '/search'
    }

    const link = `${parsed.pathname}${parsed.search}${parsed.hash}`
    return link || null
  } catch {
    return null
  }
}

function normalizeTrendLink({
  rawLink,
  topic,
  displayName,
}: {
  rawLink: string
  topic?: string | null
  displayName?: string | null
}) {
  const internalLink = toInternalTrendLink(rawLink)
  if (internalLink) {
    if (internalLink !== rawLink) {
      exploreDebug('trend link normalized', {
        from: rawLink,
        to: internalLink,
      })
    }
    return internalLink
  }

  const fallbackQuery = normalizeTrendSearchQuery(topic, displayName)
  if (fallbackQuery) {
    const fallbackLink = makeSearchLink({query: fallbackQuery})
    if (fallbackLink !== rawLink) {
      exploreDebug('trend link fallback', {
        from: rawLink,
        to: fallbackLink,
      })
    }
    return fallbackLink
  }

  return rawLink
}

export function ExploreTrendingTopics({
  selectedTab,
}: {
  selectedTab: ExploreTabKey
}) {
  const {enabled} = useTrendingConfig()
  const {trendingDisabled} = useTrendingSettings()
  const willRender = enabled && !trendingDisabled

  useEffect(() => {
    exploreDebug('gate', {
      selectedTab,
      enabled,
      trendingDisabled,
      willRender,
    })
  }, [enabled, selectedTab, trendingDisabled, willRender])

  return willRender ? <Inner selectedTab={selectedTab} /> : null
}

function Inner({selectedTab}: {selectedTab: ExploreTabKey}) {
  const {_} = useLingui()
  const {
    data: trending,
    error,
    isLoading,
    isPending,
    isRefetching,
    refetch,
  } = useGetTrendsQuery()
  const isLoadingState = isPending || isLoading || isRefetching
  const [showSlowLoadFallback, setShowSlowLoadFallback] = useState(false)
  const safeTrends = useMemo(() => {
    if (!trending?.trends) return []
    const safe: AppBskyUnspeccedDefs.TrendView[] = []
    for (const trend of trending.trends) {
      const safeLabel =
        getSafeUiLabel(trend.displayName, {context: 'explore-trend'}) ??
        getSafeUiLabel(trend.topic, {context: 'explore-trend'}) ??
        (trend.topic ? `#` : null)
      if (!safeLabel) continue
      const normalizedLink = normalizeTrendLink({
        rawLink: trend.link,
        topic: trend.topic,
        displayName: safeLabel,
      })
      if (!normalizedLink) continue
      safe.push({...trend, displayName: safeLabel, link: normalizedLink})
    }
    return safe
  }, [trending?.trends])
  const noTopics = !isLoadingState && !error && !safeTrends.length

  useEffect(() => {
    exploreDebug('module mount', {selectedTab})
  }, [selectedTab])

  useEffect(() => {
    if (!isLoadingState) {
      setShowSlowLoadFallback(false)
      return
    }
    const timer = setTimeout(() => {
      setShowSlowLoadFallback(true)
    }, 3000)
    return () => clearTimeout(timer)
  }, [isLoadingState])

  useEffect(() => {
    exploreDebug('state', {
      selectedTab,
      isPending,
      isLoading,
      isRefetching,
      hasData: Boolean(trending?.trends?.length),
      hasError: Boolean(error),
    })
  }, [
    error,
    isLoading,
    isPending,
    isRefetching,
    selectedTab,
    trending?.trends?.length,
  ])

  const onRetry = useCallback(() => {
    exploreDebug('retry')
    refetch()
  }, [refetch])

  if (isLoadingState) {
    return (
      <View style={[a.gap_md]}>
        <TrendHeroSkeleton />
        {Array.from({length: TOPIC_COUNT - 1}).map((__, i) => (
          <TrendingTopicRowSkeleton key={i} />
        ))}
        {showSlowLoadFallback ? (
          <TrendsFallback
            message={_(msg`Loading trends is taking longer than usual.`)}
            onRetry={onRetry}
          />
        ) : null}
      </View>
    )
  }

  if (error) {
    return (
      <TrendsFallback
        message={_(msg`Unable to load trends right now.`)}
        onRetry={onRetry}
      />
    )
  }

  if (!trending?.trends || noTopics) {
    return (
      <TrendsFallback
        message={_(msg`No trends available right now.`)}
        onRetry={onRetry}
      />
    )
  }

  const {hero, rest} = getFilteredTrends(safeTrends, selectedTab)

  return (
    <View style={[a.gap_md]}>
      {hero && (
        <TrendHeroCard
          trend={hero}
          onPress={() => {
            logger.metric(
              'trendingTopic:click',
              {context: 'explore'},
              {statsig: true},
            )
          }}
        />
      )}
      <View style={[a.pb_md]}>
        {rest.map((trend, index) => (
          <TrendRow
            key={trend.link}
            trend={trend}
            rank={index + 2}
            onPress={() => {
              logger.metric(
                'trendingTopic:click',
                {context: 'explore'},
                {statsig: true},
              )
            }}
          />
        ))}
      </View>
    </View>
  )
}

function TrendsFallback({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) {
  const t = useTheme()
  const {_} = useLingui()
  return (
    <View style={[a.rounded_md, a.p_md, a.gap_sm, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>{message}</Text>
      <View>
        <Button
          label={_(msg`Retry`)}
          variant="solid"
          color="secondary"
          size="small"
          onPress={onRetry}>
          <ButtonText>{_(msg`Retry`)}</ButtonText>
        </Button>
      </View>
    </View>
  )
}

function getFilteredTrends(
  trends: AppBskyUnspeccedDefs.TrendView[],
  selectedTab: ExploreTabKey,
) {
  if (selectedTab === 'for-you' || selectedTab === 'trending') {
    return {hero: trends[0], rest: trends.slice(1)}
  }

  const matches = trends.filter(trend => trendMatchesTab(trend, selectedTab))
  if (matches.length === 0) {
    return {hero: trends[0], rest: trends.slice(1)}
  }

  return {hero: matches[0], rest: matches.slice(1)}
}

function trendMatchesTab(
  trend: AppBskyUnspeccedDefs.TrendView,
  selectedTab: Exclude<ExploreTabKey, 'for-you' | 'trending'>,
) {
  const category = trend.category ?? 'other'
  const text = `${trend.displayName ?? ''} ${category}`.toLowerCase()

  if (selectedTab === 'news') {
    if (category === 'news' || category === 'politics') return true
    return [
      'news',
      'politic',
      'election',
      'government',
      'policy',
      'world',
      'breaking',
      'politique',
      'élection',
      'election',
      'gouvernement',
      'actualité',
      'actualite',
      'actu',
    ].some(keyword => text.includes(keyword))
  }

  if (selectedTab === 'sports') {
    if (category === 'sports') return true
    return [
      'sport',
      'sports',
      'nba',
      'nfl',
      'soccer',
      'football',
      'futbol',
      'tennis',
      'f1',
      'formula',
      'nhl',
      'mlb',
      'olympic',
      'uefa',
      'champions',
      'ligue',
      'basket',
      'baseball',
      'hockey',
    ].some(keyword => text.includes(keyword))
  }

  if (selectedTab === 'entertainment') {
    if (category === 'pop-culture' || category === 'video-games') return true
    return [
      'entertainment',
      'movie',
      'movies',
      'music',
      'tv',
      'show',
      'series',
      'celebrity',
      'cinema',
      'film',
      'films',
      'série',
      'serie',
      'musique',
      'gaming',
      'video game',
      'anime',
      'k-pop',
      'kpop',
    ].some(keyword => text.includes(keyword))
  }

  return false
}

function TrendHeroCard({
  trend,
  onPress,
}: {
  trend: AppBskyUnspeccedDefs.TrendView
  onPress?: () => void
}) {
  const t = useTheme()
  const {_, i18n} = useLingui()
  const {gtMobile} = useBreakpoints()
  const category = useCategoryDisplayName(trend?.category || 'other')
  const age = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(trend.startedAt || Date.now()).getTime()) /
        (1000 * 60 * 60),
    ),
  )
  const badgeType = trend.status === 'hot' ? 'hot' : age < 2 ? 'new' : null
  const postCount = trend.postCount
    ? _(
        plural(trend.postCount, {
          other: `${formatCount(i18n, trend.postCount)} posts`,
        }),
      )
    : null
  const timeLabel = _(msg`${age}h ago`)

  const gradient = t.scheme === 'light' ? 'sunrise' : 'midnight'
  const heroRatio = gtMobile ? 2.2 : 1.33
  const heroMinHeight = gtMobile ? 172 : 200
  const heroRadius = 18
  const heroPadding = 18

  return (
    <Link
      testID={trend.link}
      label={_(msg`Browse topic ${trend.displayName}`)}
      to={trend.link}
      onPress={onPress}
      style={[a.w_full]}
      PressableComponent={Pressable}>
      {({hovered, pressed}) => (
        <View
          style={[
            a.w_full,
            a.relative,
            a.overflow_hidden,
            {borderRadius: heroRadius},
            a.shadow_sm,
            {aspectRatio: heroRatio, minHeight: heroMinHeight},
            pressed && {opacity: 0.92},
            web({
              boxShadow: select(t.name, {
                light: '0 12px 28px rgba(0,0,0,0.12)',
                dark: '0 12px 28px rgba(0,0,0,0.35)',
                dim: '0 12px 28px rgba(0,0,0,0.35)',
              }),
            }),
            (hovered || pressed) &&
              web({
                boxShadow: select(t.name, {
                  light: '0 16px 34px rgba(0,0,0,0.16)',
                  dark: '0 16px 34px rgba(0,0,0,0.4)',
                  dim: '0 16px 34px rgba(0,0,0,0.4)',
                }),
                transform: [{translateY: -1}],
              }),
          ]}>
          <LinearGradientBackground
            style={[a.absolute, a.inset_0]}
            gradient={gradient}
          />
          <LinearGradient
            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.55)']}
            style={[a.absolute, a.inset_0]}
            start={[0, 0]}
            end={[0, 1]}
          />
          <SubtleHover
            hover={hovered || pressed}
            style={[{borderRadius: heroRadius}]}
          />
          <View
            style={[a.flex_1, a.justify_end, {padding: heroPadding}, a.gap_sm]}>
            <Text
              style={[
                a.text_xs,
                a.font_bold,
                {color: 'rgba(255,255,255,0.75)', letterSpacing: 0.8},
              ]}
              numberOfLines={1}>
              <Trans>Trend of the moment</Trans>
            </Text>
            <View style={[a.flex_row, a.align_center, a.gap_sm]}>
              <Text
                style={[
                  a.text_xs,
                  a.font_bold,
                  {
                    color: 'rgba(255,255,255,0.72)',
                    letterSpacing: 0.7,
                    textTransform: 'uppercase',
                  },
                ]}
                numberOfLines={1}>
                {category || _(msg`Trending`)}
              </Text>
              {badgeType && <TrendBadge type={badgeType} variant="light" />}
            </View>

            <Text
              style={[
                gtMobile ? a.text_2xl : a.text_xl,
                a.font_bold,
                {color: 'white'},
              ]}
              numberOfLines={2}>
              {trend.displayName}
            </Text>

            <View style={[a.flex_row, a.align_center, a.justify_between]}>
              <Text
                style={[a.text_sm, {color: 'rgba(255,255,255,0.8)'}]}
                numberOfLines={1}>
                {postCount}
                {postCount && timeLabel && <> &middot; </>}
                {timeLabel}
              </Text>
              <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                <View
                  style={[
                    a.align_center,
                    a.justify_center,
                    a.rounded_full,
                    {
                      height: 36,
                      paddingHorizontal: 14,
                      borderWidth: 1,
                      borderColor: 'rgba(255,255,255,0.35)',
                      backgroundColor: 'rgba(255,255,255,0.18)',
                    },
                  ]}>
                  <Text style={[a.text_sm, a.font_bold, {color: 'white'}]}>
                    <Trans>View</Trans>
                  </Text>
                </View>
                <TrendMenuButton trend={trend} />
              </View>
            </View>
          </View>
        </View>
      )}
    </Link>
  )
}

export function TrendRow({
  trend,
  rank,
  children,
  onPress,
}: ViewStyleProp & {
  trend: AppBskyUnspeccedDefs.TrendView
  rank: number
  children?: React.ReactNode
  onPress?: () => void
}) {
  const t = useTheme()
  const {_, i18n} = useLingui()

  const category = useCategoryDisplayName(trend?.category || 'other')
  const age = Math.max(
    0,
    Math.floor(
      (Date.now() - new Date(trend.startedAt || Date.now()).getTime()) /
        (1000 * 60 * 60),
    ),
  )
  const badgeType = trend.status === 'hot' ? 'hot' : age < 2 ? 'new' : null
  const postCount = trend.postCount
    ? _(
        plural(trend.postCount, {
          other: `${formatCount(i18n, trend.postCount)} posts`,
        }),
      )
    : null
  const timeLabel = _(msg`${age}h ago`)

  const actors = useModerateTrendingActors(trend.actors)

  return (
    <Link
      testID={trend.link}
      label={_(msg`Browse topic ${trend.displayName}`)}
      to={trend.link}
      onPress={onPress}
      style={[a.w_full]}
      PressableComponent={Pressable}>
      {({hovered, pressed}) => (
        <>
          <View
            style={[
              a.w_full,
              a.relative,
              a.overflow_hidden,
              a.rounded_md,
              a.py_lg,
              a.px_lg,
              a.flex_row,
              a.align_center,
              a.justify_between,
              a.gap_md,
              t.atoms.bg,
              web({
                boxShadow:
                  hovered || pressed ? '0 6px 16px rgba(0,0,0,0.08)' : 'none',
              }),
            ]}>
            <SubtleHover hover={hovered || pressed} style={[a.rounded_md]} />
            <View style={[a.flex_1, {minWidth: 0}, a.gap_sm]}>
              <View style={[a.flex_row, a.align_center, a.gap_xs]}>
                <Text
                  style={[a.text_xs, t.atoms.text_contrast_medium]}
                  numberOfLines={1}>
                  {category || _(msg`Trending`)}
                </Text>
                {badgeType && <TrendBadge type={badgeType} />}
              </View>
              <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                <Text
                  style={[a.text_xs, t.atoms.text_contrast_low, {width: 22}]}>
                  #{rank}
                </Text>
                <Text
                  style={[a.text_md, a.font_bold, a.leading_snug]}
                  numberOfLines={1}>
                  {trend.displayName}
                </Text>
              </View>
              <View
                style={[a.flex_row, a.align_center, a.gap_sm, {minHeight: 20}]}>
                {actors.length > 0 && (
                  <AvatarStack size={18} profiles={actors} />
                )}
                <Text
                  style={[
                    a.text_sm,
                    t.atoms.text_contrast_medium,
                    {fontSize: 13},
                  ]}
                  numberOfLines={1}>
                  {postCount}
                  {postCount && timeLabel && <> &middot; </>}
                  {timeLabel}
                </Text>
              </View>
            </View>
            <View style={[{width: 40, alignItems: 'flex-end'}]}>
              <TrendMenuButton trend={trend} />
            </View>
          </View>
          {children}
        </>
      )}
    </Link>
  )
}

type TrendingIndicatorType = 'hot' | 'new'

function TrendBadge({
  type,
  variant = 'default',
}: {
  type: TrendingIndicatorType
  variant?: 'default' | 'light'
}) {
  const t = useTheme()
  const {_} = useLingui()
  const pillStyles = [
    a.flex_row,
    a.align_center,
    a.gap_xs,
    a.rounded_full,
    {height: 20, paddingHorizontal: 8},
  ]

  let Icon: React.ComponentType<SVGIconProps> | null = null
  let text: string | null = null
  let color: string | null = null
  let backgroundColor: string | null = null

  switch (type) {
    case 'hot': {
      Icon = FlameIcon
      color = variant === 'light' ? 'white' : t.palette.primary_500
      backgroundColor =
        variant === 'light' ? 'rgba(255,255,255,0.18)' : t.palette.primary_50
      text = _(msg`Popular`)
      break
    }
    case 'new': {
      Icon = TrendingIcon
      text = _(msg`New`)
      color = variant === 'light' ? 'white' : t.palette.positive_700
      backgroundColor =
        variant === 'light' ? 'rgba(255,255,255,0.18)' : t.palette.positive_50
      break
    }
  }

  return (
    <View style={[pillStyles, {backgroundColor}]}>
      {Icon && <Icon size="xs" style={{color}} />}
      <Text style={[a.text_xs, a.font_medium, {color}]} numberOfLines={1}>
        {text}
      </Text>
    </View>
  )
}

function TrendMenuButton({trend}: {trend: AppBskyUnspeccedDefs.TrendView}) {
  const {_} = useLingui()

  return (
    <Menu.Root>
      <Menu.Trigger label={_(msg`Trend options`)}>
        {({props, state}) => (
          <Button
            {...props}
            label={_(msg`Trend options`)}
            size="small"
            shape="round"
            variant="ghost"
            color="secondary"
            onPress={e => {
              e.preventDefault()
              e.stopPropagation()
              props.onPress?.()
            }}
            hitSlop={HITSLOP_10}
            style={[{width: 36, height: 36}, state.pressed && {opacity: 0.7}]}>
            <ButtonIcon icon={MenuIcon} size="sm" />
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Outer>
        <Menu.Item
          label={_(msg`Hide this trend`)}
          onPress={() => {
            logger.metric(
              'trendingTopic:hide',
              {context: 'explore', trend: trend.link},
              {statsig: true},
            )
          }}>
          <Menu.ItemText>{_(msg`Hide this trend`)}</Menu.ItemText>
          <Menu.ItemIcon icon={XIcon} position="right" />
        </Menu.Item>
      </Menu.Outer>
    </Menu.Root>
  )
}

function useCategoryDisplayName(
  category: AppBskyUnspeccedDefs.TrendView['category'],
) {
  const {_} = useLingui()

  switch (category) {
    case 'sports':
      return _(msg`Sports`)
    case 'politics':
      return _(msg`Politics`)
    case 'video-games':
      return _(msg`Video Games`)
    case 'pop-culture':
      return _(msg`Entertainment`)
    case 'news':
      return _(msg`News`)
    case 'other':
    default:
      return null
  }
}

function TrendHeroSkeleton() {
  const t = useTheme()
  const {gtMobile} = useBreakpoints()
  const heroRatio = gtMobile ? 2.2 : 1.33
  const heroMinHeight = gtMobile ? 172 : 200

  return (
    <View style={[a.w_full]}>
      <View
        style={[
          {borderRadius: 18},
          t.atoms.bg_contrast_25,
          {aspectRatio: heroRatio, minHeight: heroMinHeight},
        ]}
      />
    </View>
  )
}

export function TrendingTopicRowSkeleton() {
  const t = useTheme()

  return (
    <View style={[a.w_full, a.py_sm]}>
      <View style={[a.rounded_md, t.atoms.bg_contrast_25, {height: 104}]}>
        <View style={[a.p_lg, a.gap_sm]}>
          <View style={[a.flex_row, a.gap_xs, a.align_center]}>
            <LoadingPlaceholder width={70} height={12} />
            <LoadingPlaceholder width={36} height={12} />
          </View>
          <LoadingPlaceholder width={160} height={16} />
          <LoadingPlaceholder width={140} height={12} />
        </View>
      </View>
    </View>
  )
}

function useModerateTrendingActors(
  actors: AppBskyUnspeccedDefs.TrendView['actors'],
) {
  const moderationOpts = useModerationOpts()

  return useMemo(() => {
    if (!moderationOpts) return []

    return actors
      .filter(actor => {
        const decision = moderateProfile(actor, moderationOpts)
        return !decision.ui('avatar').filter && !decision.ui('avatar').blur
      })
      .slice(0, 3)
  }, [actors, moderationOpts])
}
